import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import {
  Table,
  TableHeader,
  TableColumn,
  TableBody,
  TableRow,
  TableCell,
  Card,
  CardBody,
  Spinner,
  Button,
  Chip,
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  useDisclosure,
  addToast,
  Pagination,
  Input,
  type SortDescriptor,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { useBrand } from "@webuild-ai/react-auth";

import { SearchIcon } from "@/components/icons";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { convertFileTypeToIcon } from "@/utils/ConvertFileTypeToIcon";
import { FileTypeIcon } from "@/components/UI/FileTypeIcon";
import { PageTitle } from "@/components/UI/PageTitle";
import { siteConfig } from "@/config/site";
import { logger } from "@/telemetry";
import { MarkdownViewer } from "@/components/Citations/viewers/MarkdownViewer";

const isSafeUrl = (url: string | undefined): boolean => {
  if (!url) return false;
  try {
    return new URL(url).protocol === "https:";
  } catch {
    return false;
  }
};

const isSafePath = (path: string | undefined): boolean => {
  if (!path) return false;
  try {
    // Reject path traversal attempts
    const normalized = new URL(path, "https://placeholder.internal").pathname;

    // Detect URL-encoding tricks (e.g. %2e%2e normalising to ..)
    const inputPath = path.startsWith("/") ? path : `/${path}`;

    return (
      normalized === inputPath && !path.includes("..") && /^\/[a-zA-Z0-9/_\-%.]*$/.test(inputPath)
    );
  } catch {
    return false;
  }
};

const isValidFileId = (id: string): boolean => /^[a-zA-Z0-9_-]+$/.test(id);

const ITEMS_PER_PAGE = 20;

const getSyncStatusColor = (status: string): "success" | "warning" | "default" => {
  switch (status) {
    case "synced":
      return "success";
    case "stale":
      return "warning";
    default:
      return "default";
  }
};

interface FileCardProps {
  file: GoogleDriveFile;
  onPreview: (file: GoogleDriveFile) => void;
  onResync: (fileId: string) => void;
  onDelete: (file: GoogleDriveFile) => void;
  resyncingFileIds: Set<string>;
  deletingFileIds: Set<string>;
  t: (key: string) => string;
}

const FileCard: React.FC<FileCardProps> = ({
  file,
  onPreview,
  onResync,
  onDelete,
  resyncingFileIds,
  deletingFileIds,
  t,
}) => (
  <Card className="w-full" data-id={`gdrive-file-card-${file.id}`}>
    <CardBody className="p-4">
      <div className="flex items-start gap-3">
        <FileTypeIcon
          className="w-8 h-8 flex-shrink-0"
          icon={convertFileTypeToIcon(file.mimeType)}
        />
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold truncate" title={file.name}>
            {file.name}
          </h3>
          <p className="text-sm text-default-500 truncate">{file.mimeType}</p>
          <p className="text-sm text-default-500">{new Date(file.modifiedTime).toLocaleString()}</p>
          <Chip
            className="mt-2"
            color={getSyncStatusColor(file.syncStatus)}
            data-id={`sync-status-${file.id}`}
            size="sm"
          >
            {t(file.syncStatus)}
          </Chip>
        </div>
        <div className="flex flex-col gap-2">
          <Button
            isIconOnly
            data-id={`preview-btn-${file.id}`}
            size="sm"
            variant="light"
            onPress={() => onPreview(file)}
          >
            <Icon icon="solar:eye-linear" />
          </Button>
          <Button
            isIconOnly
            as="a"
            data-id={`gdrive-link-${file.id}`}
            href={isSafeUrl(file.googleDriveWebViewLink) ? file.googleDriveWebViewLink : undefined}
            rel="noopener noreferrer"
            size="sm"
            target="_blank"
            variant="light"
          >
            <Icon icon="solar:link-linear" />
          </Button>
          <Button
            isIconOnly
            data-id={`resync-btn-${file.id}`}
            isDisabled={resyncingFileIds.has(file.googleDriveFileId)}
            isLoading={resyncingFileIds.has(file.googleDriveFileId)}
            size="sm"
            variant="light"
            onPress={() => onResync(file.googleDriveFileId)}
          >
            <Icon icon="solar:refresh-linear" />
          </Button>
          <Button
            isIconOnly
            color="danger"
            data-id={`delete-btn-${file.id}`}
            isDisabled={deletingFileIds.has(file.id)}
            isLoading={deletingFileIds.has(file.id)}
            size="sm"
            variant="light"
            onPress={() => onDelete(file)}
          >
            <Icon icon="solar:trash-bin-minimalistic-linear" />
          </Button>
        </div>
      </div>
    </CardBody>
  </Card>
);

interface IntegrationStatus {
  connected: boolean;
  email?: string;
}

interface GoogleDriveFile {
  id: string;
  name: string;
  mimeType: string;
  size?: string | number;
  modifiedTime: string;
  googleDriveFileId: string;
  googleDriveWebViewLink: string;
  syncStatus: "synced" | "pending" | "stale";
  uploadedById: string;
}

export default function DataHubGoogleDrive() {
  const { t } = useTranslation();
  const { authenticatedFetch, platformClient } = useWebuildAPIClient();
  const { brand } = useBrand();
  const [files, setFiles] = useState<GoogleDriveFile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<GoogleDriveFile | null>(null);
  const [fileContent, setFileContent] = useState<ArrayBuffer | null>(null);
  const [loadingContent, setLoadingContent] = useState(false);
  const [resyncingFileIds, setResyncingFileIds] = useState<Set<string>>(new Set());
  const [deletingFileIds, setDeletingFileIds] = useState<Set<string>>(new Set());
  const [fileToDelete, setFileToDelete] = useState<GoogleDriveFile | null>(null);
  const [integrationStatus, setIntegrationStatus] = useState<IntegrationStatus | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
  const [searchQuery, setSearchQuery] = useState("");
  const [sortDescriptor, setSortDescriptor] = useState<SortDescriptor>({
    column: "modifiedTime",
    direction: "descending",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const debouncedSearch = useDebouncedValue(searchQuery, 300);

  // Reset page when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch]);

  const filteredFiles = useMemo(() => {
    let result = [...files];

    // Filter by search query
    if (debouncedSearch) {
      const query = debouncedSearch.toLowerCase();

      result = result.filter(
        (file) =>
          file.name.toLowerCase().includes(query) || file.mimeType.toLowerCase().includes(query),
      );
    }

    // Sort
    if (sortDescriptor.column) {
      result.sort((a, b) => {
        let aVal: string | number = "";
        let bVal: string | number = "";

        switch (sortDescriptor.column) {
          case "name":
            aVal = a.name.toLowerCase();
            bVal = b.name.toLowerCase();
            break;
          case "mimeType":
            aVal = a.mimeType.toLowerCase();
            bVal = b.mimeType.toLowerCase();
            break;
          case "size":
            aVal = typeof a.size === "number" ? a.size : parseInt(String(a.size || "0"), 10);
            bVal = typeof b.size === "number" ? b.size : parseInt(String(b.size || "0"), 10);
            break;
          case "modifiedTime":
            aVal = new Date(a.modifiedTime).getTime();
            bVal = new Date(b.modifiedTime).getTime();
            break;
          case "syncStatus":
            aVal = a.syncStatus;
            bVal = b.syncStatus;
            break;
          default:
            return 0;
        }

        const cmp = aVal < bVal ? -1 : aVal > bVal ? 1 : 0;

        return sortDescriptor.direction === "descending" ? -cmp : cmp;
      });
    }

    return result;
  }, [files, debouncedSearch, sortDescriptor]);

  const totalPages = Math.ceil(filteredFiles.length / ITEMS_PER_PAGE);
  const paginatedFiles = useMemo(() => {
    const start = (currentPage - 1) * ITEMS_PER_PAGE;

    return filteredFiles.slice(start, start + ITEMS_PER_PAGE);
  }, [filteredFiles, currentPage]);

  // Check Google Drive OAuth status via authenticatedFetch to admin API
  const checkIntegrationStatus = useCallback(async () => {
    if (!authenticatedFetch) return;

    try {
      const baseUrl = siteConfig().adminAPIURL.replace(/\/$/, "");
      const response = await authenticatedFetch(
        `${baseUrl}/admin/v1/integrations/google-drive/status`,
      );

      if (response.ok) {
        const data = await response.json();

        setIntegrationStatus(data);
      } else {
        setIntegrationStatus({ connected: false });
      }
    } catch (err) {
      logger.error("Failed to check Google Drive status", { error: String(err) });
      setIntegrationStatus({ connected: false });
    }
  }, [authenticatedFetch]);

  const loadGoogleDriveFiles = useCallback(async () => {
    if (!authenticatedFetch) return;

    setLoading(true);
    setError(null);
    try {
      const response = await authenticatedFetch(
        `${siteConfig().platformAPIURL}/platform/v1/datahub/files?index=pathway_gdrive&source=google_drive`,
      );

      // Treat 404 as "no files yet" - endpoint may not exist or no files indexed
      if (response.status === 404) {
        setFiles([]);

        return;
      }

      if (!response.ok) {
        throw new Error(`Failed to load files: ${response.status}`);
      }

      const data = await response.json();

      setFiles(data?.files || []);
    } catch (err) {
      // Don't show error for this optional feature - just show empty state
      logger.error("Failed to load Google Drive files", { error: String(err) });
      setFiles([]);
    } finally {
      setLoading(false);
    }
  }, [authenticatedFetch]);

  useEffect(() => {
    if (authenticatedFetch) {
      loadGoogleDriveFiles();
    }
  }, [authenticatedFetch, loadGoogleDriveFiles]);

  useEffect(() => {
    checkIntegrationStatus();
  }, [checkIntegrationStatus]);

  /**
   * Handle file resync operation
   * @param fileId - ID of the file to resync
   */
  const handleResync = async (fileId: string) => {
    if (!authenticatedFetch) {
      addToast({
        title: t("Operation failed"),
        description: t("Authentication not available"),
        color: "danger",
      });

      return;
    }

    // Prevent duplicate requests
    if (resyncingFileIds.has(fileId)) {
      return;
    }

    try {
      setResyncingFileIds((prev) => new Set(prev).add(fileId));

      const response = await authenticatedFetch(
        `${siteConfig().platformAPIURL}/platform/v1/datahub/google-drive/resync/${fileId}`,
        { method: "POST" },
      );

      if (response.status === 404) {
        throw new Error(t("File not found on Google Drive"));
      }

      if (response.status === 403) {
        throw new Error(t("Permission denied. Please check Google Drive access."));
      }

      if (!response.ok) {
        throw new Error(t("Unable to resync file. Server returned an error."));
      }

      // Reload list after resync
      await loadGoogleDriveFiles();
      addToast({
        title: t("Resync successful"),
        description: t("File has been resynced successfully"),
        color: "success",
      });
    } catch (err) {
      logger.error("Resync failed", { error: String(err) });
      const errorMessage =
        err instanceof Error ? err.message : t("Failed to resync file. Please try again.");

      addToast({
        title: t("Resync failed"),
        description: errorMessage,
        color: "danger",
      });
    } finally {
      setResyncingFileIds((prev) => {
        const newSet = new Set(prev);

        newSet.delete(fileId);

        return newSet;
      });
    }
  };

  const openPreview = async (file: GoogleDriveFile) => {
    setSelectedFile(file);
    setFileContent(null);
    setLoadingContent(false);
    onOpen();

    // Fetch content for markdown files (will try local then Google Drive)
    if (isMarkdownFile(file.mimeType, file.name)) {
      await fetchFileContent(file);
    }
  };

  /**
   * Check if file is a markdown file
   * @param mimeType - File MIME type
   * @param fileName - Optional file name for extension checking
   * @returns True if file should be treated as markdown
   */
  const isMarkdownFile = (mimeType: string, fileName?: string): boolean => {
    const markdownTypes = ["text/markdown", "text/x-markdown"];

    // Check by MIME type first
    if (markdownTypes.includes(mimeType)) {
      return true;
    }

    // For text/plain, also check file extension to avoid treating all plain text as markdown
    if (mimeType === "text/plain" && fileName) {
      const mdExtensions = [".md", ".markdown", ".mdown", ".mkd", ".mkdn"];

      return mdExtensions.some((ext) => fileName.toLowerCase().endsWith(ext));
    }

    return false;
  };

  /**
   * Check if file can be embedded using Google Drive's viewer
   * @param mimeType - File MIME type
   * @returns True if file can be embedded directly
   */
  const canEmbedGoogleDriveFile = (mimeType: string): boolean => {
    const embeddableTypes = [
      // Google Workspace files
      "application/vnd.google-apps.document",
      "application/vnd.google-apps.spreadsheet",
      "application/vnd.google-apps.presentation",
      "application/vnd.google-apps.form",
      "application/vnd.google-apps.drawing",
      // Common office files
      "application/pdf",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      "application/vnd.openxmlformats-officedocument.presentationml.presentation",
      "application/msword",
      "application/vnd.ms-excel",
      "application/vnd.ms-powerpoint",
      // Images
      "image/jpeg",
      "image/png",
      "image/gif",
      "image/bmp",
      "image/webp",
      // Text files (including markdown)
      "text/plain",
      "text/csv",
      "text/markdown",
      "text/x-markdown",
    ];

    return embeddableTypes.includes(mimeType);
  };

  /**
   * Get embedded preview URL for Google Drive file
   * @param googleDriveFileId - Google Drive file ID
   * @returns Embedded preview URL
   */
  const getEmbedUrl = (googleDriveFileId: string): string | null => {
    if (!isValidFileId(googleDriveFileId)) return null;

    return `https://drive.google.com/file/d/${googleDriveFileId}/preview`;
  };

  /**
   * Fetch file content for preview using temporary download URL
   * @param file - The Google Drive file to fetch content for
   * @note Backend automatically backfills Google Drive files from Drive API if not in storage
   */
  const fetchFileContent = async (file: GoogleDriveFile) => {
    if (!authenticatedFetch || !platformClient) return;

    try {
      setLoadingContent(true);

      // Get temporary download URL - backend will backfill from Google Drive if needed
      const temp = await platformClient.files.createTemporaryDownloadUrl({
        fileId: file.id,
      });

      if (!temp?.temporary_url) {
        throw new Error("No temporary URL received from server");
      }

      // Build full URL for authenticated fetch
      const baseUrl = siteConfig().platformAPIURL;
      const tempUrl = temp.temporary_url.startsWith("/")
        ? temp.temporary_url
        : `/${temp.temporary_url}`;

      if (!isSafePath(tempUrl)) {
        throw new Error("Invalid temporary URL path");
      }

      const fullUrl = `${baseUrl}${tempUrl}`;

      // Download file content
      const response = await authenticatedFetch(fullUrl);

      if (!response.ok) {
        throw new Error(`Download failed: ${response.status} ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();

      setFileContent(arrayBuffer);
    } catch (err) {
      logger.error("Failed to fetch file content", { error: String(err) });
      setFileContent(null);
    } finally {
      setLoadingContent(false);
    }
  };

  const openDeleteConfirm = (file: GoogleDriveFile) => {
    setFileToDelete(file);
    onDeleteOpen();
  };

  /**
   * Handle file deletion
   * Deletes the file from the index and file storage (not from Google Drive)
   */
  const handleDelete = async () => {
    if (!authenticatedFetch || !fileToDelete) {
      addToast({
        title: t("Operation failed"),
        description: t("Authentication not available"),
        color: "danger",
      });

      return;
    }

    // Prevent duplicate delete requests
    if (deletingFileIds.has(fileToDelete.id)) {
      return;
    }

    try {
      setDeletingFileIds((prev) => new Set(prev).add(fileToDelete.id));
      onDeleteClose();

      const response = await authenticatedFetch(
        `${siteConfig().platformAPIURL}/platform/v1/datahub/google-drive/files/${fileToDelete.id}`,
        { method: "DELETE" },
      );

      if (response.status === 404) {
        throw new Error(t("File not found"));
      }

      if (response.status === 403) {
        throw new Error(t("Permission denied"));
      }

      if (!response.ok) {
        throw new Error(t("Unable to delete file. Server returned an error."));
      }

      // Reload list after deletion
      await loadGoogleDriveFiles();
      addToast({
        title: t("File deleted"),
        description: t("File has been removed from the index successfully"),
        color: "success",
      });
    } catch (err) {
      logger.error("Delete failed", { error: String(err) });
      const errorMessage =
        err instanceof Error ? err.message : t("Failed to delete file. Please try again.");

      addToast({
        title: t("Delete failed"),
        description: errorMessage,
        color: "danger",
      });
    } finally {
      setDeletingFileIds((prev) => {
        const newSet = new Set(prev);

        newSet.delete(fileToDelete.id);

        return newSet;
      });
      setFileToDelete(null);
    }
  };

  /**
   * Format file size for display
   * @param sizeValue - File size as string, number, or undefined
   * @returns Formatted file size string (e.g., "1.5 MB")
   * @note Backend may return 0 for Google Drive files - needs investigation
   */
  const formatFileSize = (sizeValue?: string | number): string => {
    if (sizeValue === undefined || sizeValue === null) return "N/A";

    const size = typeof sizeValue === "number" ? sizeValue : parseInt(sizeValue, 10);

    if (isNaN(size) || size === 0) return "N/A";
    if (size < 1024) return `${size} B`;
    if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
    if (size < 1024 * 1024 * 1024) return `${(size / (1024 * 1024)).toFixed(1)} MB`;

    return `${(size / (1024 * 1024 * 1024)).toFixed(1)} GB`;
  };

  const embedUrl = selectedFile ? getEmbedUrl(selectedFile.googleDriveFileId) : null;

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64" data-id="gdrive-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center text-danger p-4" data-id="gdrive-error">
        <Icon className="w-8 h-8 mb-2 mx-auto" icon="solar:danger-triangle-linear" />
        <p>{error}</p>
        <Button className="mt-4" color="primary" onPress={loadGoogleDriveFiles}>
          {t("Retry")}
        </Button>
      </div>
    );
  }

  return (
    <div className="p-4 max-w-full" data-id="datahub-google-drive-page">
      <PageTitle
        centered
        dataId="datahub-gdrive-title"
        icon="logos:google-drive"
        img={`/brands/${brand.prefix || "webuild"}/knowledge.svg`}
        subtitle={t("Access and sync files from Google Drive")}
        title={t("Google Drive Files")}
      />

      {integrationStatus?.connected && (
        <div className="flex justify-end mb-6">
          <Button
            color="primary"
            data-id="refresh-gdrive-files"
            startContent={<Icon icon="solar:refresh-linear" />}
            onPress={loadGoogleDriveFiles}
          >
            {t("Refresh")}
          </Button>
        </div>
      )}

      {/* Not connected state */}
      {files.length === 0 && integrationStatus && !integrationStatus.connected && (
        <div className="text-center py-8" data-id="gdrive-not-connected">
          <Icon className="w-16 h-16 mb-4 mx-auto text-default-300" icon="logos:google-drive" />
          <p className="text-lg font-medium mb-2">{t("Connect your Google Drive")}</p>
          <p className="text-sm text-default-500 mb-6">
            {t("Connect your Google Drive account to access and sync files with the AI assistant")}
          </p>
          <Button
            as={Link}
            color="primary"
            data-id="connect-gdrive-btn"
            startContent={<Icon icon="logos:google-drive" />}
            to="/settings#integrations"
          >
            {t("Connect Google Drive")}
          </Button>
        </div>
      )}

      {/* Connected but no files state */}
      {files.length === 0 && integrationStatus?.connected && (
        <div className="text-center py-8 text-default-500" data-id="gdrive-no-files">
          <Icon
            className="w-16 h-16 mb-4 mx-auto text-default-300"
            icon="solar:folder-open-linear"
          />
          <p>{t("No Google Drive files indexed yet")}</p>
          <p className="text-sm mt-2">
            {t("Files will appear here when accessed through the AI assistant")}
          </p>
          {integrationStatus.email && (
            <div className="text-sm mt-4">
              <Chip
                color="success"
                size="sm"
                startContent={<Icon icon="solar:check-circle-linear" />}
              >
                {t("Connected as")} {integrationStatus.email}
              </Chip>
            </div>
          )}
        </div>
      )}

      {/* Files content */}
      {files.length > 0 && (
        <>
          {/* Search bar */}
          <div className="flex items-center gap-4 mb-4">
            <Input
              className="max-w-sm"
              data-id="gdrive-search-input"
              placeholder={t("Search files...")}
              startContent={<SearchIcon className="text-default-400" />}
              value={searchQuery}
              onValueChange={setSearchQuery}
            />
            <span className="text-sm text-default-500">
              {debouncedSearch
                ? t("Showing {{count}} of {{total}} files", {
                    count: filteredFiles.length,
                    total: files.length,
                  })
                : t("{{count}} files", { count: files.length })}
            </span>
          </div>

          {/* Empty search results */}
          {filteredFiles.length === 0 && debouncedSearch && (
            <div className="text-center py-8 text-default-500" data-id="gdrive-no-search-results">
              <Icon className="w-12 h-12 mb-2 mx-auto" icon="solar:magnifer-linear" />
              <p>{t("No files match your search")}</p>
            </div>
          )}

          {filteredFiles.length > 0 && (
            <>
              {/* Desktop Table View */}
              <div
                className="hidden sm:block w-full overflow-x-auto"
                data-id="gdrive-table-container"
              >
                <Table
                  aria-label={t("Google Drive files table")}
                  classNames={{
                    wrapper: "border border-divider rounded-lg w-full",
                    table: "table-fixed w-full",
                    th: "border-b border-divider",
                    td: "border-b border-divider overflow-hidden",
                  }}
                  data-id="gdrive-files-table"
                  sortDescriptor={sortDescriptor}
                  onSortChange={setSortDescriptor}
                >
                  <TableHeader>
                    <TableColumn key="name" allowsSorting className="w-[30%]">
                      {t("File Name")}
                    </TableColumn>
                    <TableColumn key="mimeType" allowsSorting className="w-[15%]">
                      {t("Type")}
                    </TableColumn>
                    <TableColumn key="size" allowsSorting className="w-[10%]">
                      {t("Size")}
                    </TableColumn>
                    <TableColumn key="modifiedTime" allowsSorting className="w-[15%]">
                      {t("Last Modified")}
                    </TableColumn>
                    <TableColumn key="syncStatus" allowsSorting className="w-[12%]">
                      {t("Sync Status")}
                    </TableColumn>
                    <TableColumn className="w-[18%]">{t("Actions")}</TableColumn>
                  </TableHeader>
                  <TableBody>
                    {paginatedFiles.map((file) => (
                      <TableRow key={file.id} data-id={`gdrive-row-${file.id}`}>
                        <TableCell>
                          <div className="flex items-center gap-2 min-w-0 max-w-full">
                            <FileTypeIcon
                              className="w-5 h-5 flex-shrink-0"
                              icon={convertFileTypeToIcon(file.mimeType)}
                            />
                            <span className="truncate min-w-0" title={file.name}>
                              {file.name}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span
                            className="text-sm text-default-500 truncate block max-w-full"
                            title={file.mimeType}
                          >
                            {file.mimeType}
                          </span>
                        </TableCell>
                        <TableCell>{formatFileSize(file.size)}</TableCell>
                        <TableCell>{new Date(file.modifiedTime).toLocaleString()}</TableCell>
                        <TableCell>
                          <Chip
                            color={getSyncStatusColor(file.syncStatus)}
                            data-id={`sync-status-${file.id}`}
                            size="sm"
                          >
                            {t(file.syncStatus)}
                          </Chip>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button
                              isIconOnly
                              data-id={`preview-btn-${file.id}`}
                              size="sm"
                              variant="light"
                              onPress={() => openPreview(file)}
                            >
                              <Icon icon="solar:eye-linear" />
                            </Button>
                            <Button
                              isIconOnly
                              as="a"
                              data-id={`gdrive-link-${file.id}`}
                              href={
                                isSafeUrl(file.googleDriveWebViewLink)
                                  ? file.googleDriveWebViewLink
                                  : undefined
                              }
                              rel="noopener noreferrer"
                              size="sm"
                              target="_blank"
                              variant="light"
                            >
                              <Icon icon="solar:link-linear" />
                            </Button>
                            <Button
                              isIconOnly
                              data-id={`resync-btn-${file.id}`}
                              isDisabled={resyncingFileIds.has(file.googleDriveFileId)}
                              isLoading={resyncingFileIds.has(file.googleDriveFileId)}
                              size="sm"
                              variant="light"
                              onPress={() => handleResync(file.googleDriveFileId)}
                            >
                              <Icon icon="solar:refresh-linear" />
                            </Button>
                            <Button
                              isIconOnly
                              color="danger"
                              data-id={`delete-btn-${file.id}`}
                              isDisabled={deletingFileIds.has(file.id)}
                              isLoading={deletingFileIds.has(file.id)}
                              size="sm"
                              variant="light"
                              onPress={() => openDeleteConfirm(file)}
                            >
                              <Icon icon="solar:trash-bin-minimalistic-linear" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Mobile Card View */}
              <div className="flex sm:hidden flex-col gap-4" data-id="gdrive-cards-container">
                {paginatedFiles.map((file) => (
                  <FileCard
                    key={file.id}
                    deletingFileIds={deletingFileIds}
                    file={file}
                    resyncingFileIds={resyncingFileIds}
                    t={t}
                    onDelete={openDeleteConfirm}
                    onPreview={openPreview}
                    onResync={handleResync}
                  />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-center mt-4" data-id="gdrive-pagination">
                  <Pagination
                    color="primary"
                    page={currentPage}
                    total={totalPages}
                    onChange={setCurrentPage}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}

      {/* Preview Modal */}
      <Modal isOpen={isOpen} size="5xl" onClose={onClose}>
        <ModalContent>
          <ModalHeader>
            <div className="flex items-center gap-2">
              <FileTypeIcon
                className="w-6 h-6"
                icon={convertFileTypeToIcon(selectedFile?.mimeType || "")}
              />
              <span className="truncate">{selectedFile?.name}</span>
            </div>
          </ModalHeader>
          <ModalBody>
            <div className="space-y-3">
              {/* Show markdown preview if content is available locally */}
              {selectedFile &&
                isMarkdownFile(selectedFile.mimeType, selectedFile.name) &&
                !loadingContent &&
                fileContent && (
                  <div className="mb-4">
                    <div className="border border-divider rounded-lg overflow-hidden">
                      <MarkdownViewer documentData={fileContent} />
                    </div>
                  </div>
                )}

              {/* Show loading state for markdown */}
              {selectedFile &&
                isMarkdownFile(selectedFile.mimeType, selectedFile.name) &&
                loadingContent && (
                  <div className="mb-4">
                    <div className="flex items-center justify-center h-48">
                      <Spinner label={t("Loading preview...")} />
                    </div>
                  </div>
                )}

              {/* Show embedded Google Drive preview for files without local content */}
              {selectedFile &&
                canEmbedGoogleDriveFile(selectedFile.mimeType) &&
                !loadingContent &&
                (!isMarkdownFile(selectedFile.mimeType, selectedFile.name) || !fileContent) &&
                embedUrl && (
                  <div className="mb-4">
                    <iframe
                      className="w-full h-[600px] border border-divider rounded-lg"
                      src={embedUrl}
                      title={selectedFile.name}
                    />
                  </div>
                )}

              {/* No preview available message - only for truly unsupported types */}
              {selectedFile &&
                !canEmbedGoogleDriveFile(selectedFile.mimeType) &&
                !loadingContent &&
                (!isMarkdownFile(selectedFile.mimeType, selectedFile.name) || !fileContent) && (
                  <div className="text-center text-default-500 py-8 mb-4">
                    <Icon className="w-12 h-12 mx-auto mb-2" icon="solar:file-text-linear" />
                    <p>{t("Preview not available for this file type")}</p>
                    <p className="text-xs mt-2">
                      {t("Click the button below to open in Google Drive")}
                    </p>
                  </div>
                )}

              {/* File metadata */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div>
                  <strong>{t("Type")}:</strong>
                </div>
                <div className="truncate" title={selectedFile?.mimeType}>
                  {selectedFile?.mimeType}
                </div>

                <div>
                  <strong>{t("Size")}:</strong>
                </div>
                <div>{selectedFile?.size ? formatFileSize(selectedFile.size) : "N/A"}</div>

                <div>
                  <strong>{t("Last Modified")}:</strong>
                </div>
                <div>
                  {selectedFile?.modifiedTime &&
                    new Date(selectedFile.modifiedTime).toLocaleString()}
                </div>

                <div>
                  <strong>{t("Sync Status")}:</strong>
                </div>
                <div>
                  <Chip color={getSyncStatusColor(selectedFile?.syncStatus || "")} size="sm">
                    {selectedFile?.syncStatus && t(selectedFile.syncStatus)}
                  </Chip>
                </div>
              </div>

              <Button
                as="a"
                className="w-full mt-4"
                color="primary"
                href={
                  isSafeUrl(selectedFile?.googleDriveWebViewLink)
                    ? selectedFile?.googleDriveWebViewLink
                    : undefined
                }
                rel="noopener noreferrer"
                startContent={<Icon icon="logos:google-drive" />}
                target="_blank"
                variant="bordered"
              >
                {t("Open in Google Drive")}
              </Button>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="danger" variant="light" onPress={onClose}>
              {t("Close")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal isOpen={isDeleteOpen} onClose={onDeleteClose}>
        <ModalContent>
          <ModalHeader className="flex items-center gap-2">
            <Icon className="w-6 h-6 text-danger" icon="solar:trash-bin-minimalistic-linear" />
            <span>{t("Delete File")}</span>
          </ModalHeader>
          <ModalBody>
            <p>
              {t("Are you sure you want to delete")} <strong>{fileToDelete?.name}</strong>?
            </p>
            <p className="text-sm text-default-500 mt-2">
              {t(
                "This will remove the file from the index and file storage. The original file in Google Drive will not be affected.",
              )}
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="light" onPress={onDeleteClose}>
              {t("Cancel")}
            </Button>
            <Button color="danger" data-id="confirm-delete-btn" onPress={handleDelete}>
              {t("Delete")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
